import { createTheme, alpha } from '@mui/material/styles';

// ─── MARQIT ANALYTICS — design tokens ──────────────────────────────────────────
// One brand teal (the logo accent) and darker shades of the same hue for filled controls and
// small teal text, so contrast on white stays readable. The interface is light only.
export const BRAND_TEAL = '#19C6C0';
export const BRAND_TEAL_DARK = '#11BDB6';

export const tokens = {
  brand: BRAND_TEAL,
  primary: '#0F9A93', // filled buttons, active states
  primaryDark: '#0B7F7A', // teal text on white, hover
  primarySoft: '#E4F6F4', // active nav item, soft chips
  primaryTint: '#F0FAF9', // hover backgrounds
  bg: '#F4F9F8',
  paper: '#FFFFFF',
  ink: '#0F1F1E', // main text and dark accents
  inkSoft: '#5E6E6D', // secondary text
  inkMuted: '#8A9897',
  border: '#E1ECEA',
  borderStrong: '#CFE0DE',
  success: '#12A06A',
  successSoft: '#E5F6EE',
  error: '#D14350',
  errorSoft: '#FCEBEC',
  // Plus and minus in reports: a touch deeper than success/error so numbers in tables stay calm and readable.
  positive: '#13865C',
  positiveSoft: '#E9F6F0',
  negative: '#C2404D',
  negativeSoft: '#FBEDEE',
  warning: '#B86E00',
  warningSoft: '#FFF4E0',
  info: '#1F6FD1',
  infoSoft: '#E8F1FC',
  profile: '#0F3B3C', // dark teal profile card
  heroGradient: 'linear-gradient(115deg, #EAF8F6 0%, #DDF3EF 55%, #D0EEE9 100%)',
  shadowCard: '0 1px 2px rgba(15, 31, 30, 0.04), 0 8px 24px rgba(15, 31, 30, 0.05)',
  shadowRaised: '0 2px 6px rgba(15, 31, 30, 0.06), 0 16px 36px rgba(15, 31, 30, 0.09)',
  radius: 16,
  radiusSm: 12,
};

// Marketplace accents: charts and small marks only; the interface itself stays teal.
export const marketplaceColors = {
  wildberries: { main: '#B516A0', soft: '#F8E8F5', label: 'Wildberries', short: 'WB', gradient: 'linear-gradient(145deg, #CB11AB 0%, #7B1BA8 100%)' },
  ozon: { main: '#005BFF', soft: '#E6EEFF', label: 'Ozon', short: 'OZ', gradient: 'linear-gradient(145deg, #2F7BFF 0%, #0047D6 100%)' },
  yandex_market: { main: '#F29100', soft: '#FFF3DE', label: 'Яндекс Маркет', short: 'ЯМ', gradient: 'linear-gradient(145deg, #FFC400 0%, #FF5A26 100%)' },
};

export const marketplaceMeta = (mp) =>
  marketplaceColors[mp] || { main: tokens.primary, soft: tokens.primarySoft, label: mp, short: '?', gradient: tokens.primary };

const FONT_FAMILY = '"Inter", "SF Pro Display", -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif';

export function getTheme() {
  const t = tokens;
  const focusRing = `0 0 0 3px ${alpha(t.brand, 0.4)}`;
  return createTheme({
    palette: {
      mode: 'light',
      primary: { main: t.primary, dark: t.primaryDark, light: t.primarySoft, contrastText: '#FFFFFF' },
      secondary: { main: t.ink, contrastText: '#FFFFFF' },
      background: { default: t.bg, paper: t.paper },
      success: { main: t.success, light: t.successSoft },
      error: { main: t.error, light: t.errorSoft },
      warning: { main: t.warning, light: t.warningSoft },
      info: { main: t.info, light: t.infoSoft },
      text: { primary: t.ink, secondary: t.inkSoft, disabled: t.inkMuted },
      divider: t.border,
    },
    shape: { borderRadius: t.radiusSm },
    typography: {
      fontFamily: FONT_FAMILY,
      h1: { fontWeight: 700, fontSize: '2.125rem', letterSpacing: '-0.02em', lineHeight: 1.2 },
      h2: { fontWeight: 700, fontSize: '1.5rem', letterSpacing: '-0.01em', lineHeight: 1.25 },
      h3: { fontWeight: 700, fontSize: '1.25rem', lineHeight: 1.3 },
      h4: { fontWeight: 700, fontSize: '1.625rem', letterSpacing: '-0.01em' },
      h5: { fontWeight: 700, fontSize: '1.125rem' },
      h6: { fontWeight: 600, fontSize: '1rem' },
      subtitle1: { fontWeight: 600 },
      subtitle2: { fontWeight: 600, fontSize: '0.875rem' },
      body1: { fontSize: '0.9375rem', lineHeight: 1.55 },
      body2: { fontSize: '0.875rem', lineHeight: 1.5 },
      caption: { fontSize: '0.8125rem', lineHeight: 1.45 },
      overline: { fontWeight: 700, fontSize: '0.75rem', letterSpacing: '0.16em', lineHeight: 1.6 },
      button: { fontWeight: 600, textTransform: 'none', letterSpacing: '0.005em' },
    },
    components: {
      MuiCssBaseline: {
        styleOverrides: `
          * { box-sizing: border-box; }
          body { background: ${t.bg}; font-family: ${FONT_FAMILY}; font-variant-numeric: tabular-nums; }
          ::-webkit-scrollbar { width: 8px; height: 8px; }
          ::-webkit-scrollbar-track { background: transparent; }
          ::-webkit-scrollbar-thumb { background: ${alpha(t.ink, 0.14)}; border-radius: 4px; }
          a { color: ${t.primaryDark}; }
          a:focus-visible { outline: 2px solid ${t.brand}; outline-offset: 2px; border-radius: 4px; }
        `,
      },
      MuiPaper: {
        defaultProps: { elevation: 0 },
        styleOverrides: {
          root: { backgroundImage: 'none' },
          rounded: { borderRadius: t.radius },
          outlined: { borderColor: t.border },
        },
      },
      MuiCard: {
        defaultProps: { elevation: 0 },
        styleOverrides: { root: { borderRadius: t.radius, border: `1px solid ${t.border}`, boxShadow: t.shadowCard } },
      },
      MuiCardContent: { styleOverrides: { root: { padding: 24, '&:last-child': { paddingBottom: 24 } } } },
      MuiButtonBase: { styleOverrides: { root: { '&.Mui-focusVisible': { boxShadow: focusRing } } } },
      MuiButton: {
        defaultProps: { disableElevation: true },
        styleOverrides: {
          root: { borderRadius: t.radiusSm, padding: '9px 18px', fontSize: '0.9375rem' },
          sizeSmall: { padding: '6px 12px', fontSize: '0.875rem' },
          containedPrimary: { '&:hover': { backgroundColor: t.primaryDark } },
          outlined: {
            borderColor: t.borderStrong,
            color: t.ink,
            backgroundColor: t.paper,
            '&:hover': { borderColor: t.primary, backgroundColor: t.primaryTint },
          },
          outlinedError: { color: t.error, borderColor: alpha(t.error, 0.4) },
          text: { '&:hover': { backgroundColor: t.primaryTint } },
          textPrimary: { color: t.primaryDark },
        },
      },
      MuiIconButton: { styleOverrides: { root: { borderRadius: 10, '&:hover': { backgroundColor: t.primaryTint } } } },
      MuiToggleButton: {
        styleOverrides: {
          root: {
            textTransform: 'none',
            fontWeight: 600,
            fontSize: '0.9375rem',
            color: t.inkSoft,
            borderColor: t.border,
            padding: '8px 16px',
            '&.Mui-selected': {
              backgroundColor: t.primary,
              color: '#FFFFFF',
              '&:hover': { backgroundColor: t.primaryDark },
            },
          },
        },
      },
      MuiOutlinedInput: {
        styleOverrides: {
          root: {
            borderRadius: t.radiusSm,
            backgroundColor: t.paper,
            '& .MuiOutlinedInput-notchedOutline': { borderColor: t.borderStrong },
            '&:hover .MuiOutlinedInput-notchedOutline': { borderColor: t.primary },
            '&.Mui-focused .MuiOutlinedInput-notchedOutline': { borderColor: t.primary, borderWidth: 2 },
          },
        },
      },
      MuiInputLabel: { styleOverrides: { root: { '&.Mui-focused': { color: t.primaryDark } } } },
      MuiChip: {
        styleOverrides: {
          root: { borderRadius: 8, fontWeight: 600 },
          colorPrimary: { backgroundColor: t.primarySoft, color: t.primaryDark },
        },
      },
      MuiAlert: {
        styleOverrides: {
          root: { borderRadius: t.radiusSm, alignItems: 'flex-start' },
          standardInfo: { backgroundColor: t.infoSoft },
          standardWarning: { backgroundColor: t.warningSoft },
          standardError: { backgroundColor: t.errorSoft },
          standardSuccess: { backgroundColor: t.successSoft },
        },
      },
      MuiTableContainer: { styleOverrides: { root: { borderRadius: t.radiusSm } } },
      MuiTableCell: {
        styleOverrides: {
          root: { borderBottom: `1px solid ${t.border}`, padding: '12px 14px', fontSize: '0.875rem' },
          head: { color: t.inkSoft, fontWeight: 600, fontSize: '0.8125rem', backgroundColor: t.paper, whiteSpace: 'nowrap' },
        },
      },
      MuiTableRow: { styleOverrides: { root: { '&.MuiTableRow-hover:hover': { backgroundColor: t.primaryTint } } } },
      MuiTableSortLabel: { styleOverrides: { root: { '&.Mui-active': { color: t.ink } } } },
      MuiDialog: { styleOverrides: { paper: { borderRadius: 20, boxShadow: t.shadowRaised } } },
      MuiDialogTitle: { styleOverrides: { root: { fontWeight: 700, fontSize: '1.2rem', padding: '20px 24px 8px' } } },
      MuiDialogActions: { styleOverrides: { root: { padding: '12px 24px 20px' } } },
      MuiTooltip: {
        defaultProps: { arrow: true },
        styleOverrides: {
          tooltip: { backgroundColor: t.ink, fontSize: '0.8125rem', lineHeight: 1.45, padding: '8px 12px', borderRadius: 8, maxWidth: 360 },
          arrow: { color: t.ink },
        },
      },
      MuiLinearProgress: {
        styleOverrides: {
          root: { borderRadius: 4, height: 8, backgroundColor: t.primarySoft },
          bar: { borderRadius: 4, backgroundColor: t.primary },
        },
      },
      MuiCircularProgress: { styleOverrides: { colorPrimary: { color: t.primary } } },
      MuiDivider: { styleOverrides: { root: { borderColor: t.border } } },
      MuiTab: {
        styleOverrides: { root: { textTransform: 'none', fontWeight: 600, minHeight: 44, '&.Mui-selected': { color: t.primaryDark } } },
      },
      MuiTabs: { styleOverrides: { indicator: { backgroundColor: t.primary, height: 3, borderRadius: 3 } } },
      MuiListItemButton: {
        styleOverrides: { root: { borderRadius: t.radiusSm, '&.Mui-focusVisible': { boxShadow: `inset ${focusRing}` } } },
      },
      MuiMenu: { styleOverrides: { paper: { borderRadius: t.radiusSm, boxShadow: t.shadowRaised, border: `1px solid ${t.border}` } } },
      MuiPopover: { styleOverrides: { paper: { borderRadius: t.radius, boxShadow: t.shadowRaised, border: `1px solid ${t.border}` } } },
      MuiSkeleton: { styleOverrides: { root: { backgroundColor: alpha(t.ink, 0.06) } } },
    },
  });
}

export default getTheme;
